import { useState, useEffect } from 'react'
import { useAuth } from '../stores/auth'
import { updateOrganization, supabase } from '@unvld/db'
import { toast } from 'sonner'
import { useSearchParams } from 'react-router-dom'
import { Save, CreditCard, ExternalLink, Palette, Shield, Globe, Sparkles, Check, ArrowRight } from 'lucide-react'
import { LogoUpload } from '../components/LogoUpload'
import { useTierGate } from '../hooks/useTierGate'

const PLANS = [
  {
    tier: 'starter' as const,
    name: 'Starter',
    price: 49,
    platformFee: '3,9% + 0,50 €',
    features: [
      'Bis zu 3 Mitarbeiter',
      'Event-Management',
      'Ticket-Verkauf & QR Check-in',
      'Gästeverwaltung',
      'White-Label Website',
      'E-Mail-Support',
    ],
  },
  {
    tier: 'professional' as const,
    name: 'Professional',
    price: 149,
    platformFee: '2,5% + 0,30 €',
    popular: true,
    features: [
      'Bis zu 10 Mitarbeiter',
      'Alles aus Starter',
      'Detaillierte Analytics',
      'Kampagnen & Newsletter',
      'Custom Branding',
      'API-Zugang',
      'Prioritäts-Support',
    ],
  },
  {
    tier: 'enterprise' as const,
    name: 'Enterprise',
    price: 349,
    platformFee: '1,5% + 0,20 €',
    features: [
      'Unbegrenzte Mitarbeiter',
      'Alles aus Professional',
      'Custom Domain',
      'Multi-Venue Support',
      'Dedizierter Account Manager',
      'SLA & Onboarding',
    ],
  },
]

export function SettingsPage() {
  const { org, staff } = useAuth()
  const { tier, isTrialing } = useTierGate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [saving, setSaving] = useState(false)
  const [upgradingTier, setUpgradingTier] = useState<string | null>(null)
  const [name, setName] = useState(org?.name ?? '')
  const [primaryColor, setPrimaryColor] = useState(org?.branding?.primary_color ?? '#1A1A2E')
  const [secondaryColor, setSecondaryColor] = useState(org?.branding?.secondary_color ?? '#E94560')
  const [theme, setTheme] = useState(org?.branding?.theme ?? 'elegant')

  useEffect(() => {
    if (searchParams.get('subscription') === 'success') {
      toast.success('Abo erfolgreich aktiviert! Vielen Dank.')
      setSearchParams({}, { replace: true })
    }
  }, [searchParams, setSearchParams])

  const handleUpgrade = async (targetTier: string) => {
    if (targetTier === tier) return
    setUpgradingTier(targetTier)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-create-subscription`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ tier: targetTier }),
      })
      const data = await res.json()
      if (data.url) {
        window.location.href = data.url
      } else {
        toast.error(data.error || 'Upgrade fehlgeschlagen')
      }
    } catch {
      toast.error('Verbindungsfehler')
    }
    setUpgradingTier(null)
  }

  const handleSave = async () => {
    if (!org) return
    setSaving(true)
    try {
      await updateOrganization(org.id, {
        name,
        branding: { ...org.branding, primary_color: primaryColor, secondary_color: secondaryColor, theme },
      })
      toast.success('Einstellungen gespeichert')
    } catch (err: any) {
      toast.error(err.message)
    }
    setSaving(false)
  }

  const handleStripeOnboard = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-onboard`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json',
        },
      })
      const data = await res.json()
      if (data.url) {
        window.open(data.url, '_blank')
      } else {
        toast.error(data.error || 'Stripe-Einrichtung fehlgeschlagen')
      }
    } catch {
      toast.error('Verbindungsfehler')
    }
  }

  if (!org) return null

  return (
    <div className="max-w-3xl space-y-8">
      <h1 className="text-2xl font-bold text-white">Einstellungen</h1>

      {/* Club Info */}
      <section className="bg-slate-900 border border-slate-800 rounded-xl p-6 space-y-4">
        <div className="flex items-center gap-2 text-white mb-2">
          <Globe className="w-5 h-5 text-indigo-400" />
          <h2 className="text-lg font-semibold">Club-Informationen</h2>
        </div>

        <div>
          <label className="block text-sm text-slate-400 mb-1.5">Logo</label>
          <LogoUpload orgId={org.id} currentLogoUrl={org.logo_url} />
        </div>

        <div>
          <label className="block text-sm text-slate-400 mb-1.5">Club-Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        <div>
          <label className="block text-sm text-slate-400 mb-1.5">URL</label>
          <div className="flex items-center gap-2">
            <span className="px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-slate-400 text-sm">
              https://{org.slug}.unvld.me
            </span>
            <a
              href={`https://${org.slug}.unvld.me`}
              target="_blank"
              className="p-2.5 text-slate-400 hover:text-white border border-slate-700 rounded-lg"
            >
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>

        <div>
          <label className="block text-sm text-slate-400 mb-1.5">Abo</label>
          <div className="flex items-center gap-2">
            <span className="px-3 py-1.5 bg-indigo-600/20 text-indigo-400 rounded-full text-sm font-medium capitalize">
              {org.subscription_tier}
            </span>
            {org.founding_club && (
              <span className="px-3 py-1.5 bg-amber-500/20 text-amber-400 rounded-full text-sm font-medium">
                Founding Club (-{(org.founding_discount * 100).toFixed(0)}%)
              </span>
            )}
          </div>
        </div>
      </section>

      {/* Branding */}
      <section className="bg-slate-900 border border-slate-800 rounded-xl p-6 space-y-4">
        <div className="flex items-center gap-2 text-white mb-2">
          <Palette className="w-5 h-5 text-violet-400" />
          <h2 className="text-lg font-semibold">Branding</h2>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-slate-400 mb-1.5">Primärfarbe</label>
            <div className="flex items-center gap-2">
              <input type="color" value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)} className="w-10 h-10 rounded cursor-pointer border-0" />
              <input type="text" value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)} className="flex-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm" />
            </div>
          </div>
          <div>
            <label className="block text-sm text-slate-400 mb-1.5">Akzentfarbe</label>
            <div className="flex items-center gap-2">
              <input type="color" value={secondaryColor} onChange={(e) => setSecondaryColor(e.target.value)} className="w-10 h-10 rounded cursor-pointer border-0" />
              <input type="text" value={secondaryColor} onChange={(e) => setSecondaryColor(e.target.value)} className="flex-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm" />
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm text-slate-400 mb-1.5">Theme</label>
          <div className="grid grid-cols-4 gap-2">
            {['elegant', 'modern', 'bold', 'classic'].map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTheme(t)}
                className={`px-4 py-2.5 rounded-lg text-sm font-medium capitalize transition-colors ${
                  theme === t
                    ? 'bg-indigo-600 text-white'
                    : 'bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700'
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Stripe / Payment */}
      <section className="bg-slate-900 border border-slate-800 rounded-xl p-6 space-y-4">
        <div className="flex items-center gap-2 text-white mb-2">
          <CreditCard className="w-5 h-5 text-emerald-400" />
          <h2 className="text-lg font-semibold">Zahlungen (Stripe)</h2>
        </div>

        {org.stripe_onboarding_complete ? (
          <div className="flex items-center gap-3 p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
            <Shield className="w-5 h-5 text-emerald-400" />
            <div>
              <p className="text-sm text-emerald-400 font-medium">Stripe ist eingerichtet</p>
              <p className="text-xs text-emerald-400/60">Zahlungen und Auszahlungen sind aktiv. Descriptor: "VNVLD Entertainment"</p>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-slate-400">
              Verbinde Stripe, um Zahlungen zu empfangen. Die Einrichtung dauert ca. 5 Minuten.
              Auf deinen Kontoauszügen erscheint diskret "VNVLD Entertainment".
            </p>
            <button
              onClick={handleStripeOnboard}
              className="flex items-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium rounded-lg transition-colors"
            >
              <CreditCard className="w-4 h-4" />
              Stripe einrichten
              <ExternalLink className="w-3 h-3" />
            </button>
          </div>
        )}
      </section>

      {/* Plan & Abrechnung */}
      <section className="bg-[#131316] border border-white/5 rounded-xl p-6 space-y-6">
        <div className="flex items-center gap-2 text-white mb-2">
          <Sparkles className="w-5 h-5 text-amber-400" />
          <h2 className="text-lg font-semibold">Plan & Abrechnung</h2>
        </div>

        {/* Current plan info */}
        <div className="flex items-center gap-3 flex-wrap">
          <span className="px-3 py-1.5 bg-indigo-600/20 text-indigo-400 rounded-full text-sm font-medium capitalize">
            {tier}
          </span>
          {org?.founding_club && (
            <span className="px-3 py-1.5 bg-amber-500/20 text-amber-400 rounded-full text-sm font-medium">
              Founding Club (-{(org.founding_discount * 100).toFixed(0)}%)
            </span>
          )}
          {isTrialing && org?.trial_ends_at && (
            <span className="px-3 py-1.5 bg-violet-500/20 text-violet-400 rounded-full text-sm font-medium">
              Testphase endet am {new Date(org.trial_ends_at).toLocaleDateString('de-DE', { day: 'numeric', month: 'long', year: 'numeric' })}
              {' '}({Math.max(0, Math.ceil((new Date(org.trial_ends_at).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))} Tage verbleibend)
            </span>
          )}
        </div>

        {/* Plan comparison cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {PLANS.map((plan) => {
            const isCurrent = plan.tier === tier
            const isUpgrade = PLANS.findIndex((p) => p.tier === tier) < PLANS.findIndex((p) => p.tier === plan.tier)

            return (
              <div
                key={plan.tier}
                className={`relative rounded-xl border p-5 transition-colors ${
                  plan.popular
                    ? 'border-indigo-500/50 bg-indigo-500/5'
                    : 'border-white/5 bg-white/[0.02]'
                } ${isCurrent ? 'ring-1 ring-indigo-500/30' : ''}`}
              >
                {plan.popular && (
                  <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-3 py-0.5 bg-indigo-600 text-white text-xs font-medium rounded-full">
                    Beliebt
                  </div>
                )}

                <div className="mb-4">
                  <h3 className="text-base font-semibold text-white">{plan.name}</h3>
                  <div className="mt-2 flex items-baseline gap-1">
                    <span className="text-3xl font-bold text-white">{plan.price}</span>
                    <span className="text-sm text-slate-400">EUR/Monat</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">+ {plan.platformFee} Plattformgebühr</p>
                </div>

                <ul className="space-y-2 mb-5">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2 text-sm text-slate-300">
                      <Check className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>

                {isCurrent ? (
                  <div className="w-full py-2.5 text-center text-sm font-medium text-indigo-400 bg-indigo-500/10 rounded-lg">
                    Aktueller Plan
                  </div>
                ) : isUpgrade ? (
                  <button
                    onClick={() => handleUpgrade(plan.tier)}
                    disabled={upgradingTier === plan.tier}
                    className="w-full flex items-center justify-center gap-2 py-2.5 text-sm font-medium bg-white text-black rounded-lg hover:bg-white/90 disabled:opacity-50 transition-colors"
                  >
                    {upgradingTier === plan.tier ? (
                      <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        Upgraden
                        <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </button>
                ) : (
                  <div className="w-full py-2.5 text-center text-sm text-slate-500 bg-white/5 rounded-lg">
                    Inkludiert
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </section>

      {/* Save */}
      <button
        onClick={handleSave}
        disabled={saving}
        className="flex items-center gap-2 px-6 py-3 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white font-medium rounded-xl transition-colors"
      >
        {saving ? (
          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
        ) : (
          <Save className="w-5 h-5" />
        )}
        Speichern
      </button>
    </div>
  )
}
